// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import uuid from 'uuid';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Dialog, DialogActions, DialogContent, TextField } from '@material-ui/core';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import { IsEmpty } from '../../../common/checks';
import { Trim } from '../../../common/util';
import TagTransferList from './transferList';
import MText from '../../common/MText';
import MButton from '../../common/MButton';
import Colours from '../../../styles/colours';
import OverLoading from '../../global/OverLoading';

const GroupModal = ({ onClose, group }) => {
  const [name, setName] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!IsEmpty(group) && typeof group === 'object') {
      setName(group.name || '');
      setSelectedTags(group.tags || []);
    }

    const fetchTags = async () => {
      try {
        const popularTags = await apiService.getPopularTags();
        setAvailableTags(popularTags || []);
      } catch (error) {
        toast.error('Failed to load popular tags');
      }
    };

    fetchTags();
  }, [group]);

  const handleSave = async () => {
    const trimmedName = Trim(name, false, true);

    if (IsEmpty(trimmedName)) {
      toast.error('Group name is required');
      return;
    }

    setLoading(true);
    try {
      // Fetch current grouping data
      const currentGroups = await apiService.getPopularTagGrouping();
      let newGroups = Array.isArray(currentGroups) ? currentGroups : [];

      const duplicate = _.find(
        newGroups,
        (g) => g.name.toLowerCase() === trimmedName.toLowerCase() && (IsEmpty(group) || g.id !== group.id),
      );

      if (!IsEmpty(duplicate)) {
        toast.error('Group name already exists');
        setLoading(false);
        return;
      }

      if (!IsEmpty(group)) {
        // Edit mode
        newGroups = newGroups.map((g) => {
          if (g.id === group.id) {
            return { ...g, name: trimmedName, tags: selectedTags };
          }
          return g;
        });
      } else {
        // Create mode
        const newGroup = {
          id:   uuid.v4(),
          name: trimmedName,
          tags: selectedTags,
        };
        newGroups.push(newGroup);
      }

      await apiService.updatePopularTagGrouping(newGroups);
      toast.success(`Group ${group ? 'updated' : 'created'} successfully`);
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <OverLoading />}
      <Dialog fullWidth maxWidth="sm" open aria-labelledby="alert-dialog-title">
        <DialogContent>
          <MText text={!IsEmpty(group) ? 'Edit Tag Group' : 'Create Tag Group'} size={24} className="mb-3" />

          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mb-4"
          />

          <MText text="Tags Selection" size={16} bold className="mt-3 mb-2" />

          <TagTransferList
            availableTags={availableTags}
            selectedTags={selectedTags}
            onChangeTags={(tags) => setSelectedTags(tags)}
          />

        </DialogContent>
        <DialogActions>
          <MButton
            onClick={onClose}
            colour="transparent"
            textColour={Colours.MaterialBlue}
            variant="text"
            text="Cancel"
            disabled={loading}
          />
          <MButton
            onClick={handleSave}
            colour={Colours.MaterialBlue}
            textColour={Colours.White}
            variant="contained"
            text="Save"
            disabled={loading}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

GroupModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  group:   PropTypes.shape({
    id:   PropTypes.string,
    name: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
  }),
};

GroupModal.defaultProps = {
  group: null,
};

export default GroupModal;
